// ─── SCHEDULE PUBLISH ─────────────────────────────────────────────────────────
// Writes read-only schedule snapshots to families/{id}/schedule/{yyyy-mm-dd}
// so token-link caregivers (who have no Google auth and therefore no GCal
// access) can see the family's days. Built from already-fetched Review &
// Assign day objects; FOH metadata blocks and raw descriptions are stripped.
//
// Doc shape:
//   { date, events: [{ title, startTime, allDay, assignee }],
//     pickups: [{ childId, childName, time, adultId, date, label, source }],
//     publishedAt }

import { fsSet } from '../lib/firebase';
import { pickupsForDate } from '../lib/pickups';
import { getEventAssignee, stripAssigneeTag } from './assignments';
import { fetchScheduleWindow } from './scheduleSnapshot';

// Snapshot one Review & Assign day object (see LookAheadReview fetchData)
export function buildScheduleDoc(day, children = [], { pickupsActive = true } = {}) {
  // day.pickups (when present) is already active-filtered by the caller —
  // the fallback only re-derives it, so it needs the flag too.
  const pickups = (day.pickups || pickupsForDate(children, day.isoDate, null, { active: pickupsActive }))
    .filter(p => !p.skipped)
    .map(p => ({
      childId: p.childId, childName: p.childName, time: p.time,
      adultId: p.adultId, date: p.date, leg: p.leg || 'pickup',
      label: p.label || 'School pickup', source: p.source || 'default',
    }));
  return {
    date: day.isoDate,
    events: (day.events || []).map(e => ({
      title:     stripAssigneeTag(e.summary),
      startTime: e.startTime || 'All day',   // display-ready, e.g. "8:00–9:30 AM"
      allDay:    !e._raw?.start?.dateTime,
      assignee:  getEventAssignee(e),
    })),
    pickups,
    publishedAt: new Date().toISOString(),
  };
}

// Publish a set of Review & Assign days. Returns how many were written.
export async function publishScheduleDays(familyId, days, children = [], { pickupsActive = true } = {}) {
  let published = 0;
  for (const day of (days || [])) {
    if (!day?.isoDate) continue;
    await fsSet(`families/${familyId}/schedule/${day.isoDate}`, buildScheduleDoc(day, children, { pickupsActive }));
    published++;
  }
  return published;
}

// ─── BATCHED AUTO-PUBLISH ──────────────────────────────────────────────────────
// Grove is Spark tier — no server/Cloud Functions — so this batching lives
// entirely in the browser tab. Every action that changes what caregivers
// should see (Lock In, a "Running late" unassign, a recurring-series
// reassignment, ignoring an event, editing a pickup routine) calls
// scheduleBatchedPublish() instead of publishing immediately. The first call
// in a quiet period starts a fixed 90s window; anything else that calls in
// during that window just rides along (the eventual flush re-fetches the
// CURRENT state, so it doesn't matter what triggered it); a call after the
// window already closed starts a fresh one. This turns a burst of edits
// (assigning five events during one Lock In session, say) into one write
// instead of five.
//
// Risk this doesn't fully solve: if the tab closes before the 90s is up, the
// batch is lost — there's no server to fall back on. markScheduleDirty()
// writes a cheap `scheduleDirtySince` timestamp to the family doc synchronously
// (before the debounce), so ANY session that later loads this family and
// finds that flag stale (checkScheduleCatchup, called on Home mount) can
// flush it — a safety net, not a replacement for the timer.

const BATCH_WINDOW_MS = 90_000;
// Must clear BATCH_WINDOW_MS with real margin — a second tab/session running
// checkScheduleCatchup shouldn't race and double-publish a batch that's still
// legitimately pending in the tab that started it.
const CATCHUP_STALE_MS = 150_000;

let pendingTimer = null;
let pendingFamilyData = null;

export async function markScheduleDirty(familyId) {
  if (!familyId) return;
  try {
    await fsSet(`families/${familyId}`, { scheduleDirtySince: new Date().toISOString() });
  } catch (err) {
    console.warn('[schedulePublish] markScheduleDirty failed:', err.message);
  }
}

async function clearScheduleDirty(familyId) {
  try {
    await fsSet(`families/${familyId}`, { scheduleDirtySince: null, lastPublishedAt: new Date().toISOString() });
  } catch (err) {
    console.warn('[schedulePublish] clearScheduleDirty failed:', err.message);
  }
}

async function flushSchedule(familyData) {
  const familyId = familyData?.id;
  if (!familyId) return;
  try {
    const days = await fetchScheduleWindow(familyData);
    await publishScheduleDays(familyId, days, familyData?.children || [], {
      pickupsActive: familyData?.config?.pickupsActive !== false,
    });
    await clearScheduleDirty(familyId);
  } catch (err) {
    console.warn('[schedulePublish] batched publish failed:', err.message);
  }
}

// Call after any action that should eventually reach caregivers. Marks dirty
// immediately (cheap), then (re)uses the pending 90s window if one's already
// running, or starts a new one. `familyData` is stashed fresh on every call
// so the eventual flush uses whatever's most current when it fires.
export function scheduleBatchedPublish(familyData) {
  const familyId = familyData?.id;
  if (!familyId) return;
  markScheduleDirty(familyId);
  pendingFamilyData = familyData;
  if (pendingTimer) return; // a batch is already running — this just rides along
  pendingTimer = setTimeout(() => {
    const data = pendingFamilyData;
    pendingTimer = null;
    pendingFamilyData = null;
    flushSchedule(data);
  }, BATCH_WINDOW_MS);
}

// Manual "Publish schedule" button — supersedes any pending batch (no reason
// to publish again 90s later for the same data) and publishes right away
// with the days the caller already has in hand.
export async function publishNowAndCancelBatch(familyId, days, children, opts) {
  if (pendingTimer) { clearTimeout(pendingTimer); pendingTimer = null; pendingFamilyData = null; }
  const published = await publishScheduleDays(familyId, days, children, opts);
  await clearScheduleDirty(familyId);
  return published;
}

// Called once per family load (e.g. on Home mount) to recover from a batch
// that never flushed — tab closed, phone locked, etc. Only acts if the dirty
// flag is old enough that no legitimately-pending timer could still own it.
export async function checkScheduleCatchup(familyData) {
  const dirtySince = familyData?.scheduleDirtySince;
  if (!dirtySince) return;
  const ageMs = Date.now() - new Date(dirtySince).getTime();
  if (ageMs < CATCHUP_STALE_MS) return;
  await flushSchedule(familyData);
}
